import { Controller } from "@hotwired/stimulus"

// Pre-submit safety net for the PO review screen. Scans every row at
// submit time and intercepts when the operator is about to create a
// brand-new standalone product for a line where the backend detected a
// likely family parent (Assignment 4 rule).
//
// Flow:
//   1. The form's submit fires this controller's `check` action
//      (intercepted before Turbo's turbo_confirm so we can ask first).
//   2. We walk every <tr data-po-row-detected-family-id="..."> with a
//      non-empty family id, look at its action <select>, and collect
//      any rows where the action is "create_new".
//   3. If the collected list is non-empty, we open the dialog with the
//      offending rows + their detected family, and stop the submit.
//   4. The operator chooses:
//        - "Go back & fix"   → close dialog, do nothing (form stays open)
//        - "Create as new anyway" → close dialog, set a guard flag, and
//          re-fire the submit (which now sails past the check)
//   5. If no offending rows, submit proceeds normally.
//
// Targets:
//   dialog   - the <dialog> element
//   rowList  - <ul> we populate with the row descriptions + detected family
export default class extends Controller {
    static targets = ["dialog", "rowList"]

    connect() {
        // Store the bound handler so disconnect() removes the SAME reference —
        // a fresh .bind(this) is a different function and removeEventListener
        // would no-op, leaking the capture-phase submit guard across Turbo swaps.
        this._boundCheck = this.check.bind(this)
        this.element.addEventListener("submit", this._boundCheck, { capture: true })
        this._bypass = false
    }

    disconnect() {
        this.element.removeEventListener("submit", this._boundCheck, { capture: true })
    }

    check(event) {
        if (this._bypass) {
            this._bypass = false
            return // Let the form submit
        }

        const offenders = this._collectOffenders()
        if (offenders.length === 0) {
            return // Nothing to warn about
        }

        event.preventDefault()
        event.stopPropagation()
        this._render(offenders)
        if (this.hasDialogTarget && this.dialogTarget.showModal) {
            this.dialogTarget.showModal()
        }
    }

    cancel() {
        if (this.hasDialogTarget) this.dialogTarget.close()
    }

    proceed() {
        if (this.hasDialogTarget) this.dialogTarget.close()
        this._bypass = true
        // requestSubmit so the form fires submit events normally — which
        // re-runs our check, sees the bypass flag, and lets it through.
        this.element.requestSubmit()
    }

    // ── private ─────────────────────────────────────────────────────────

    _collectOffenders() {
        const rows = this.element.querySelectorAll("[data-po-row-detected-family-id]")
        const offenders = []
        rows.forEach((row) => {
            const familyId   = row.getAttribute("data-po-row-detected-family-id")
            const familyName = row.getAttribute("data-po-row-detected-family-name")
            if (!familyId) return

            const actionSelect = row.querySelector('select[name*="[action]"]')
            if (!actionSelect) return
            if (actionSelect.value !== "create_new") return

            // Use the description cell text (first table cell holds it).
            const descCell = row.querySelector("td")
            const desc = descCell ? descCell.textContent.trim().split("\n")[0] : "(line item)"

            offenders.push({ desc, familyName })
        })
        return offenders
    }

    _render(offenders) {
        if (!this.hasRowListTarget) return
        this.rowListTarget.innerHTML = ""
        offenders.forEach(({ desc, familyName }) => {
            const li = document.createElement("li")
            li.style.marginBottom = "0.375rem"
            li.innerHTML = `<strong>${this._escape(desc)}</strong> &mdash; looks like <em>${this._escape(familyName)}</em>`
            this.rowListTarget.appendChild(li)
        })
    }

    _escape(str) {
        const div = document.createElement("div")
        div.textContent = str
        return div.innerHTML
    }
}
